import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import {
  authApi,
  getToken,
  setToken,
  type Impersonating,
  type User,
} from "./api";

interface AuthState {
  user: User | null;
  loading: boolean;
  impersonating: Impersonating | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => void;
  startImpersonation: (token: string, user: User) => void;
  stopImpersonation: () => void;
  refresh: () => Promise<void>;
}

const ADMIN_TOKEN_KEY = "tshirtbar_admin_token";

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [impersonating, setImpersonating] = useState<Impersonating | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    if (!getToken()) {
      setUser(null);
      setImpersonating(null);
      setLoading(false);
      return;
    }
    try {
      const { user, impersonating } = await authApi.me();
      setUser(user);
      setImpersonating(impersonating);
    } catch {
      setToken(null);
      setUser(null);
      setImpersonating(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function login(email: string, password: string) {
    const { token, user } = await authApi.login(email, password);
    setToken(token);
    setUser(user);
    setImpersonating(null);
  }

  async function register(email: string, password: string, name: string) {
    const { token, user } = await authApi.register(email, password, name);
    setToken(token);
    setUser(user);
    setImpersonating(null);
  }

  function logout() {
    setToken(null);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setUser(null);
    setImpersonating(null);
  }

  function startImpersonation(token: string, target: User) {
    // Stash the real superadmin token so we can restore it.
    const current = getToken();
    if (current) localStorage.setItem(ADMIN_TOKEN_KEY, current);
    setToken(token);
    setUser(target);
    refresh();
  }

  function stopImpersonation() {
    const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (adminToken) {
      setToken(adminToken);
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      refresh();
    }
  }

  return (
    <AuthCtx.Provider
      value={{
        user,
        loading,
        impersonating,
        login,
        register,
        logout,
        startImpersonation,
        stopImpersonation,
        refresh,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
